"use client";

import { useQuery } from "@tanstack/react-query";

import { useTranslations } from "next-intl";

import { api } from "@/shared/lib/api";
import { ChampionIcon } from "@/shared/ui/data-display/champion-icon";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/data-display/table";
import { QUERY_VOLATILITY } from "@/shared/config/api-constants";

type ChampionStat = {
  championName: string;
  games: number;
  wins: number;
  winrate: number;
  avgKda: string;
  topPlayers?: {
    playerId: string;
    displayName: string;
    games: number;
    wins: number;
  }[];
};

/** Сколько строк показываем — таблица здесь вспомогательная, не главный экран. */
const MAX_ROWS = 10;

async function fetchChampionStats(tournamentId: string) {
  const res = await api.get<ChampionStat[]>(
    `/tournaments/${encodeURIComponent(tournamentId)}/champion-stats`,
  );
  return res.data ?? [];
}

/**
 * Статистика по чемпионам за турнир. Блок не рендерится, когда данных нет:
 * по этому турниру статистика заведомо частичная (собирается только с
 * десктоп-приложения), а пустая таблица читается как сломанная страница.
 * О самой неполноте честно сообщает TournamentStatsCoverageNote.
 */
export function TournamentChampionStatsCard({
  tournamentId,
}: {
  tournamentId: string;
}) {
  const t = useTranslations("TournamentStats.champions");

  const { data: champions = [], isLoading } = useQuery({
    queryKey: ["tournaments", tournamentId, "champion-stats"],
    queryFn: () => fetchChampionStats(tournamentId),
    enabled: Boolean(tournamentId),
    ...QUERY_VOLATILITY.STANDARD,
    retry: false,
  });

  if (isLoading || champions.length === 0) return null;

  const rows = [...champions]
    .sort((a, b) => b.games - a.games || b.winrate - a.winrate)
    .slice(0, MAX_ROWS);

  return (
    <div className="rounded-[14px] border border-border bg-card p-5 shadow-sm">
      <div className="mb-1 flex items-baseline justify-between gap-3">
        <h2 className="font-heading text-lg font-bold">{t("title")}</h2>
        <span className="text-xs text-muted-foreground tabular-nums">
          {t("total", { count: champions.length })}
        </span>
      </div>
      <p className="mb-4 text-sm text-muted-foreground">
        {t("note")}
      </p>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("champion")}</TableHead>
              <TableHead className="text-right">{t("games")}</TableHead>
              <TableHead className="text-right">{t("wins")}</TableHead>
              <TableHead className="text-right">WR</TableHead>
              <TableHead className="text-right">{t("avgKda")}</TableHead>
              <TableHead className="hidden sm:table-cell">{t("mostOften")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((c) => {
              const topPlayer = [...(c.topPlayers ?? [])].sort(
                (a, b) => b.games - a.games,
              )[0];
              return (
                <TableRow key={c.championName}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="relative h-7 w-7 shrink-0 overflow-hidden rounded">
                        <ChampionIcon
                          name={c.championName}
                          size={28}
                          className="size-full scale-[1.15] object-cover"
                        />
                      </span>
                      <span className="truncate font-medium">
                        {c.championName}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {c.games}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {c.wins}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {Math.round(c.winrate)}%
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {c.avgKda}
                  </TableCell>
                  <TableCell className="hidden max-w-[160px] truncate text-muted-foreground sm:table-cell">
                    {topPlayer ? topPlayer.displayName : "—"}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
